// src/components/ProtectedRoute/Admin/Dialogs/DialogRepository.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
} from "@mui/material";
import supabase from "../../../../utils/supabase";
import BaseDialog from "./Framework/BaseDialog";

const TAG = "[DialogRepository]";

/**
 * Repository add/edit dialog using the common BaseDialog frame.
 * - Table: public."Repositories"
 * - Fields: uuid (PK, default gen), acronym, name_en, parent_id (FK -> Repositories.uuid),
 *           taxapad_code, country, city
 *
 * Props:
 *  - open: boolean
 *  - onClose: (didSave:boolean, savedRow?:object)=>void
 *  - repository: row object to edit (optional)
 *  - initialValue: string (prefill acronym when adding)
 */
export default function DialogRepository({
  open,
  onClose,
  repository = null,
  initialValue = "",
}) {
  const isEdit = !!repository;

  const [form, setForm] = useState({
    acronym: "",
    name_en: "",
    parent_id: null,
    taxapad_code: "",
    country: "",
    city: "",
  });

  const [repos, setRepos] = useState([]); // [{uuid, acronym, name_en}]
  const [countries, setCountries] = useState([]); // ["Japan", ...] or your countries.id values
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---- helpers ----
  const handleChange = (field, value) =>
    setForm((p) => ({ ...p, [field]: value }));

  const parentValue = useMemo(
    () => repos.find((r) => r.uuid === form.parent_id) || null,
    [repos, form.parent_id]
  );

  const countryOptions = useMemo(() => {
    const arr = Array.isArray(countries) ? countries.slice() : [];
    arr.sort((a, b) => String(a).localeCompare(String(b)));
    return arr;
  }, [countries]);

  // ---- load masters on open ----
  useEffect(() => {
    if (!open) return;

    let mounted = true;

    async function fetchRepos() {
      const { data, error } = await supabase
        .from("Repositories")
        .select("uuid, acronym, name_en")
        .order("acronym", { ascending: true });
      if (!mounted) return;
      if (error) {
        console.warn(`${TAG} fetch repos failed`, error);
        setRepos([]);
      } else {
        setRepos(data || []);
      }
    }

    async function fetchCountries() {
      const { data, error } = await supabase.from("countries").select("id");
      if (!mounted) return;
      if (error) {
        console.warn(`${TAG} fetch countries failed`, error);
        setCountries([]);
      } else {
        setCountries((data || []).map((c) => c.id));
      }
    }

    async function primeForm() {
      setLoading(true);
      try {
        await Promise.all([fetchRepos(), fetchCountries()]);
        if (!mounted) return;

        if (isEdit) {
          // Refresh the target row for freshest data (avoid nested selects to be safe)
          const { data, error } = await supabase
            .from("Repositories")
            .select("*")
            .eq("uuid", repository.uuid)
            .maybeSingle();
          if (error) {
            console.warn(`${TAG} fetch target failed`, error);
          }
          const row = data || repository;
          setForm({
            acronym: row?.acronym || "",
            name_en: row?.name_en || "",
            parent_id: row?.parent_id || null,
            taxapad_code: row?.taxapad_code || "",
            country: row?.country || "",
            city: row?.city || "",
          });
        } else {
          setForm({
            acronym: initialValue || "",
            name_en: "",
            parent_id: null,
            taxapad_code: "",
            country: "",
            city: "",
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    primeForm();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, repository?.uuid, initialValue]);

  // ---- save ----
  async function handleSubmit() {
    // minimal validation
    const acronym = (form.acronym || "").trim();
    if (!acronym) {
      alert("Acronym is required.");
      return;
    }

    setSaving(true);
    try {
      const saveData = {
        acronym,
        name_en: (form.name_en || "").trim() || null,
        parent_id: form.parent_id || null,
        taxapad_code: (form.taxapad_code || "").trim() || null,
        country: form.country || null,
        city: (form.city || "").trim() || null,
      };

      if (isEdit) {
        // Update, then fetch the saved row
        const { error: upErr } = await supabase
          .from("Repositories")
          .update(saveData)
          .eq("uuid", repository.uuid);
        if (upErr) throw upErr;

        const { data: row, error: getErr } = await supabase
          .from("Repositories")
          .select("*")
          .eq("uuid", repository.uuid)
          .maybeSingle();
        if (getErr) throw getErr;

        onClose?.(true, row || { ...saveData, uuid: repository.uuid });
        return;
      }

      // Insert new
      const { data: inserted, error: insErr } = await supabase
        .from("Repositories")
        .insert([saveData])
        .select("*")
        .maybeSingle();
      if (insErr) throw insErr;

      let final = inserted;
      // If no parent specified, set self as parent (valid name)
      if (inserted && !inserted.parent_id) {
        const { data: upd, error: selfErr } = await supabase
          .from("Repositories")
          .update({ parent_id: inserted.uuid })
          .eq("uuid", inserted.uuid)
          .select("*")
          .maybeSingle();
        if (selfErr) throw selfErr;
        final = upd || inserted;
      }

      onClose?.(true, final);
    } catch (e) {
      console.error(`${TAG} save failed`, e);
      alert(`Error saving repository: ${e.message || e}`);
      onClose?.(false, null);
    } finally {
      setSaving(false);
    }
  }

  // ---- UI ----
  return (
    <BaseDialog
      open={open}
      title={isEdit ? "Edit Repository" : "Add New Repository"}
      onClose={() => onClose?.(false)}
      onSubmit={handleSubmit}
      submitText={isEdit ? "Save" : "Create"}
      loading={loading || saving}
      maxWidth="sm"
    >
      <Box sx={{ px: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <CircularProgress />
          </Box>
        ) : null}

        <TextField
          autoFocus
          margin="dense"
          label="Acronym (e.g., NIAES, NARO)"
          fullWidth
          required
          value={form.acronym}
          onChange={(e) => handleChange("acronym", e.target.value)}
        />

        <TextField
          margin="dense"
          label="Official Name (English)"
          fullWidth
          value={form.name_en}
          onChange={(e) => handleChange("name_en", e.target.value)}
        />

        <TextField
          margin="dense"
          label="Taxapad Code"
          fullWidth
          value={form.taxapad_code}
          onChange={(e) => handleChange("taxapad_code", e.target.value)}
        />

        <Autocomplete
          sx={{ mt: 1 }}
          options={countryOptions}
          value={form.country || null}
          onChange={(_, v) => handleChange("country", v || "")}
          isOptionEqualToValue={(o, v) => String(o) === String(v)}
          renderOption={(props, option) => {
            const { key, ...rest } = props; // avoid "key in spread" warning
            return (
              <li key={key} {...rest}>
                {option}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Country" margin="dense" fullWidth />
          )}
        />

        <TextField
          margin="dense"
          label="City"
          fullWidth
          value={form.city}
          onChange={(e) => handleChange("city", e.target.value)}
        />

        <Autocomplete
          sx={{ mt: 2 }}
          options={repos}
          value={parentValue}
          onChange={(_, row) => handleChange("parent_id", row ? row.uuid : null)}
          isOptionEqualToValue={(o, v) => o?.uuid === v?.uuid}
          getOptionLabel={(o) =>
            o ? `${o.acronym || ""}${o.name_en ? ` — ${o.name_en}` : ""}` : ""
          }
          renderOption={(props, option) => {
            const { key, ...rest } = props; // avoid "key in spread" warning
            return (
              <li key={key} {...rest}>
                {option.acronym || ""} {option.name_en ? `— ${option.name_en}` : ""}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Valid Name (Parent)"
              margin="dense"
              helperText="If this is a synonym, select its current valid name. If this is valid, leave blank."
            />
          )}
        />

        {!isEdit && (
          <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
            Tip: Leaving “Valid Name (Parent)” empty will automatically set this record as its own
            valid name.
          </Typography>
        )}
      </Box>
    </BaseDialog>
  );
}
